import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Locale, translate, TranslationKey } from '../i18n/translations';

const STORAGE_KEY = 'brianna-dk-locale';

/** Path for locale: English uses no prefix (/), Danish uses /da. subpath is '' for home, 'blog', 'blog/id', 'resume'. */
function pathForLocale(locale: Locale, subpath: string): string {
  const base = subpath ? `/${subpath}` : '';
  return locale === 'da' ? `/da${base || '/'}` : (base || '/');
}

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
  pathFor: (subpath: string) => string;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function getInitialLocale(): Locale {
  if (typeof window !== 'undefined' && window.location?.pathname) {
    const match = window.location.pathname.match(/^\/(da)(?:\/|$)/);
    if (match) return 'da';
  }
  return 'en';
}

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // ignore
    }
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = locale === 'da' ? 'da' : 'en';
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
  }, []);

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string | number>) => translate(locale, key, params),
    [locale]
  );

  const pathFor = useCallback((subpath: string) => pathForLocale(locale, subpath), [locale]);

  const value: LocaleContextValue = { locale, setLocale, t, pathFor };

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}
